import { type AirHandlerUnit, type Floor, type FullAirHandlerUnit } from "@eisox/api/core-api";

import { compareAirHandlerUnitNames, getParentAirHandlerUnits } from "./air-handler-unit.helper";

export const getAirHandlerUnitsByGroup = (airHandlerUnits: AirHandlerUnit[]): Record<string, AirHandlerUnit[]> =>
  airHandlerUnits.reduce(
    (acc, ahu) => {
      if (ahu.group) {
        acc[ahu.group] = [...(acc[ahu.group] || []), ahu];
      } else {
        acc[ahu.id] = [ahu];
      }
      return acc;
    },
    {} as Record<string, AirHandlerUnit[]>,
  );

type GroupableAirHandlerUnit = Pick<FullAirHandlerUnit, "id" | "name" | "group">;

export const getFullAirHandlerUnitsByGroup = <T extends GroupableAirHandlerUnit>(
  airHandlerUnits: T[],
): Record<string, T[]> =>
  airHandlerUnits.reduce(
    (acc, ahu) => {
      if (ahu.group) {
        acc[ahu.group] = [...(acc[ahu.group] || []), ahu];
      } else {
        acc[ahu.id] = [ahu];
      }
      return acc;
    },
    {} as Record<string, T[]>,
  );

export const getAirHandlerUnitGroupName = (airHandlerUnits: GroupableAirHandlerUnit[]): string => {
  const firstAirHandlerUnit = airHandlerUnits[0];
  if (!firstAirHandlerUnit) return "";
  return airHandlerUnits.length > 1
    ? (firstAirHandlerUnit.group ?? firstAirHandlerUnit.name)
    : firstAirHandlerUnit.name;
};

export const getSortedFullAirHandlerUnitGroups = <T extends GroupableAirHandlerUnit>(airHandlerUnits: T[]): T[][] =>
  Object.values(getFullAirHandlerUnitsByGroup(airHandlerUnits)).sort((a, b) =>
    compareAirHandlerUnitNames(getAirHandlerUnitGroupName(a), getAirHandlerUnitGroupName(b)),
  );

/** The air handler unit the right-column selector lists first, so entry points land on it. */
export const getFirstSelectableAirHandlerUnit = <
  T extends GroupableAirHandlerUnit & Pick<FullAirHandlerUnit, "airHandlerUnitParentId">,
>(
  airHandlerUnits: T[],
): T | undefined =>
  getSortedFullAirHandlerUnitGroups(getParentAirHandlerUnits(airHandlerUnits))[0]?.[0] ?? airHandlerUnits[0];

export const getAirHandlerUnitsGroups = (airHandlerUnits: AirHandlerUnit[]): { name: string; items: string[] }[] =>
  airHandlerUnits
    .reduce<{ name: string; items: string[] }[]>((acc, ahu) => {
      const existingGroup = acc.find(item => item.name === ahu.group);
      if (existingGroup) {
        existingGroup.items.push(ahu.id);
      } else if (ahu.group) {
        acc.push({ name: ahu.group, items: [ahu.id] });
      }

      return acc;
    }, [])
    .sort((a, b) => compareAirHandlerUnitNames(a.name, b.name));

export const getFloors = (airHandlerUnits: FullAirHandlerUnit[], floors: Floor[]): Floor[] => {
  const floorIds = [...new Set(airHandlerUnits.map(ahu => ahu.floor?.id).filter(floorId => floorId !== undefined))];
  return floorIds.map(floorId => floors.find(floor => floor.id === floorId)).filter(floor => floor !== undefined);
};
